"use client";

import { useMemo } from "react";
import { useApiWithLayout } from "@/hooks/api/useApiWithLayout";
import { SSO_PROVIDER, type SsoProviderId } from "@/components/SSOAuthButton/types";
import { PostLoginResponseData } from "@/interface/api/postLogin/types";
import { PostRegisterResponseData } from "@/interface/api/postRegister/types";

import postLoginGooglePostHandler from "@/interface/api/postLoginGoogle";
import postRegisterGooglePostHandler from "@/interface/api/postRegisterGoogle";
import postSignInApplePostHandler from "@/interface/api/postSignInApple";
import postSignUpApplePostHandler from "@/interface/api/postSignUpApple";
import { ApiError } from "@/utils/apiErrorHandler";

export interface SSOApiResult<TData = unknown, TPayload = unknown> {
  request: (payload: TPayload) => void;
  loading: boolean;
  data: TData | undefined;
  error: ApiError | null;
  reset: () => void;
}

export interface UseSSOApiManagerResult {
  loginApi: SSOApiResult;
  registerApi: SSOApiResult;
}

/**
 * Hook that provides API handlers for different SSO providers.
 */
export const useSSOApiManager = (provider: SsoProviderId): UseSSOApiManagerResult => {
  const apiHandlers = useMemo(() => {
    const googleHandlers = {
      login: postLoginGooglePostHandler.postLoginGoogle,
      register: postRegisterGooglePostHandler.postRegisterGoogle,
    };
    const appleHandlers = {
      login: postSignInApplePostHandler.postSignInApple,
      register: postSignUpApplePostHandler.postSignUpApple,
    };
    return {
      [SSO_PROVIDER.GOOGLE]: googleHandlers,
      [SSO_PROVIDER.APPLE]: appleHandlers,
    };
  }, []);

  // Get handlers for current provider
  const handlers = apiHandlers[provider];

  // Login API hook
  const loginApiResult = useApiWithLayout<PostLoginResponseData>(
    handlers?.login ||
      (() => Promise.reject(new Error(`Login API not implemented for provider: ${provider}`)))
  );

  // Register API hook
  const registerApiResult = useApiWithLayout<PostRegisterResponseData>(
    handlers?.register ||
      (() => Promise.reject(new Error(`Register API not implemented for provider: ${provider}`)))
  );

  return {
    loginApi: {
      request: loginApiResult.request,
      loading: loginApiResult.loading,
      data: loginApiResult.data,
      error: loginApiResult.error,
      reset: loginApiResult.reset,
    },
    registerApi: {
      request: registerApiResult.request,
      loading: registerApiResult.loading,
      data: registerApiResult.data,
      error: registerApiResult.error,
      reset: registerApiResult.reset,
    },
  };
};
